import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FiGlobe, FiAlertCircle } from 'react-icons/fi'
import { gigCategoryApi, GigCategory } from '@/services/api/gigCategories'
import { popularSkillsApi } from '@/services/api/popularSkills'
import { useGetPlatformStatsQuery } from '@/services/statsApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const CategoriesPage: React.FC = () => {
    const [categories, setCategories] = useState<GigCategory[]>([])
    const [popularSkills, setPopularSkills] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [skillsLoading, setSkillsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')

    // Fetch platform stats for the statistics section
    const { data: platformStats, isLoading: isLoadingStats, error: statsError } = useGetPlatformStatsQuery()

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await gigCategoryApi.getCategories()
                if (response.data?.categories) {
                    setCategories(response.data.categories)
                }
            } catch (err: any) {
                console.error('Error fetching categories:', err)
                setError(err.response?.data?.error?.message || 'Failed to load categories. Please try again.')
            } finally {
                setLoading(false)
            }
        }

        fetchCategories()
    }, [])

    // Fetch popular skills from API
    useEffect(() => {
        const fetchPopularSkills = async () => {
            try {
                setSkillsLoading(true)
                const response = await popularSkillsApi.getPopularSkills(12)
                if (response.data?.skills) {
                    setPopularSkills(response.data.skills)
                }
            } catch (err: any) {
                console.error('Error fetching popular skills:', err)
                // Fallback to empty array on error, the section will still render
                setPopularSkills([])
            } finally {
                setSkillsLoading(false)
            }
        }

        fetchPopularSkills()
    }, [])

    // Filter categories based on search query
    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )

    /**
     * Convert hex color to RGB values and generate Tailwind-compatible background class
     * Uses opacity and generates dynamic styles based on API color data
     */
    const getCategoryColorClass = (color?: string): string => {
        if (!color) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'

        // Map hex colors to Tailwind classes (enhanced palette)
        const colorMap: Record<string, string> = {
            '#10B981': 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
            '#3B82F6': 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            '#8B5CF6': 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
            '#EF4444': 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
            '#F59E0B': 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
            '#EC4899': 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
            '#06B6D4': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400',
            '#84CC16': 'bg-lime-100 dark:bg-lime-900/30 text-lime-600 dark:text-lime-400',
            '#F97316': 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
            '#6366F1': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
            '#64748B': 'bg-slate-100 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400',
            '#6B7280': 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
        }

        return colorMap[color] || 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
    }

    // Use API-fetched skills or fallback to default skills if empty
    const displaySkills = popularSkills.length > 0 ? popularSkills : [
        "React Development", "Logo Design", "Content Writing", "SEO Optimization",
        "Mobile App Development", "Social Media Management", "Video Editing", "Data Analysis",
        "WordPress Development", "Brand Strategy", "Translation Services", "Digital Marketing"
    ]

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-gray-900 dark:text-white mb-4 sm:mb-6">Explore All Categories</h1>
                    <p className="text-base sm:text-lg md:text-body-lg max-w-3xl mx-auto mb-6 sm:mb-8 text-gray-700 dark:text-gray-300">
                        Discover opportunities across diverse categories and find the perfect gigs
                        that match your skills and interests. From technology to creative services,
                        there's something for everyone on SideGO.
                    </p>
                    <div className="max-w-md mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search categories..."
                                className="form-input pl-10"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <FiGlobe className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Categories Grid */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Loading State */}
                    {loading && (
                        <div className="flex justify-center items-center py-20">
                            <LoadingSpinner />
                        </div>
                    )}

                    {/* Error State */}
                    {error && !loading && (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-start gap-4">
                            <FiAlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-semibold text-red-800 dark:text-red-300 mb-1">Error Loading Categories</h3>
                                <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && !error && filteredCategories.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-600 dark:text-gray-400 text-lg">
                                {searchQuery ? 'No categories found matching your search.' : 'No categories available at this time.'}
                            </p>
                        </div>
                    )}

                    {/* Categories Grid */}
                    {!loading && !error && filteredCategories.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
                            {filteredCategories.map((category) => (
                                <div
                                    key={category._id}
                                    className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 shadow-sm hover:shadow-md transition-shadow duration-200 group border border-gray-200 dark:border-gray-700"
                                >
                                    {/* Icon and Title Row */}
                                    <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                                        {/* Icon Container - Emoji or placeholder */}
                                        <div className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex-shrink-0 text-2xl sm:text-3xl ${getCategoryColorClass(category.color)}`}>
                                            {category.icon || '📁'}
                                        </div>

                                        {/* Category Name */}
                                        <h3 className="text-lg sm:text-xl md:heading-card text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors font-semibold">
                                            {category.name}
                                        </h3>
                                    </div>

                                    {/* Description */}
                                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                                        {category.description || 'Explore opportunities in this category.'}
                                    </p>

                                    {/* Subcategories */}
                                    {category.subcategories && category.subcategories.length > 0 && (
                                        <div className="mb-3 sm:mb-4">
                                            <div className="flex flex-wrap gap-1.5 sm:gap-2">
                                                {category.subcategories.slice(0, 3).map((sub, subIndex) => (
                                                    <span
                                                        key={subIndex}
                                                        className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs sm:text-xs px-2 py-0.5 sm:py-1 rounded-full"
                                                    >
                                                        {sub}
                                                    </span>
                                                ))}
                                                {category.subcategories.length > 3 && (
                                                    <span className="inline-block text-gray-500 dark:text-gray-400 text-xs px-2 py-0.5 sm:py-1">
                                                        +{category.subcategories.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Footer: Stats and CTA */}
                                    <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                            {category.gigCount !== undefined ? `${category.gigCount} gigs` : 'View gigs'}
                                        </span>
                                        <Link
                                            to={`/gigs?category=${encodeURIComponent(category.slug)}`}
                                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-xs sm:text-sm font-medium"
                                        >
                                            Browse Gigs →
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Featured Skills Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">Popular Skills</h2>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                            These are some of the most in-demand skills on our platform.
                            Click on any skill to find related gigs.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                        {skillsLoading ? (
                            <div className="flex justify-center items-center py-8">
                                <LoadingSpinner />
                            </div>
                        ) : displaySkills.length > 0 ? (
                            displaySkills.map((skill, index) => (
                                <Link
                                    key={index}
                                    to={`/gigs?skill=${encodeURIComponent(skill.toLowerCase())}`}
                                    className="inline-block bg-white dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 transition-colors duration-200"
                                >
                                    {skill}
                                </Link>
                            ))
                        ) : (
                            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">No popular skills available at this time.</p>
                        )}
                    </div>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">SideGO by the Numbers</h2>
                        <p className="text-base sm:text-lg md:text-body-lg max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                            Join a thriving community of professionals and businesses making things happen.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-8">
                        {/* Active Gigs */}
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl font-heading font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                                {isLoadingStats ? (
                                    <span className="animate-pulse">...</span>
                                ) : statsError ? (
                                    '10,000+'
                                ) : (
                                    `${platformStats?.data.totalGigs.toLocaleString()}+`
                                )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Active Gigs
                            </div>
                        </div>

                        {/* Registered Users */}
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl font-heading font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                                {isLoadingStats ? (
                                    <span className="animate-pulse">...</span>
                                ) : statsError ? (
                                    '25,000+'
                                ) : (
                                    `${platformStats?.data.totalUsers.toLocaleString()}+`
                                )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Registered Users
                            </div>
                        </div>

                        {/* Countries */}
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl font-heading font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                                {isLoadingStats ? (
                                    <span className="animate-pulse">...</span>
                                ) : statsError ? (
                                    '50+'
                                ) : (
                                    `${platformStats?.data.countries}+`
                                )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Countries
                            </div>
                        </div>

                        {/* Satisfaction Rate */}
                        <div className="text-center">
                            <div className="text-3xl sm:text-4xl font-heading font-bold text-primary-600 dark:text-primary-400 mb-1 sm:mb-2">
                                {isLoadingStats ? (
                                    <span className="animate-pulse">...</span>
                                ) : statsError ? (
                                    '95%'
                                ) : (
                                    `${platformStats?.data.successRate}%`
                                )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Satisfaction Rate
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Ready to Start Your Journey?</h2>
                    <p className="text-base sm:text-lg md:text-body-lg mb-6 sm:mb-8 text-gray-700 dark:text-gray-300">
                        Whether you're looking to hire talent or offer your services,
                        SideGO has the perfect category for you.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                        <Link to="/gigs" className="btn-hero bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600">
                            Browse All Gigs
                        </Link>
                        <Link to="/register" className="btn-hero bg-transparent border-2 border-primary-600 dark:border-primary-500 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20">
                            Get Started
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default CategoriesPage